import * as React from "react";
import "styled-components/macro";
import { connect } from "react-redux";
import classnames from "classnames";
import Space from "styled-space";
import { Flex } from "@rebass/grid";
import {
  Button,
  Classes,
  Icon,
  InputGroup,
  ButtonGroup,
  Intent,
  Text,
} from "@blueprintjs/core";
import * as R from "ramda";

import { ThunkDispatch } from "../../../types/redux";
import {
  ClientDimension,
  Dimension,
  ClientDimensionInstance as ClientDimensionInstanceModal,
} from "../../../types/models";

import {
  clientDimensionActions,
  createClientDimensionData,
} from "src/store/modules/client_dimensions";
import { ClientDimensionInstance } from "./ClientDimensionInstance";
import Toaster from "src/components/molecules/Toaster";
import { MAX_INSTANCE_NAME_LENGTH } from "src/constants/validation";

export const DEFAULT_CLIENT_DIMENSION_NAME =
  "!__DEFAULT_CLIENT_DIMENSION_NAME__";
export const DEFAULT_CLIENT_DIMENSION_INSTANCE_NAME =
  "!__DEFAULT_CLIENT_INSTANCE_NAME__";

const columnCSS = {
  borderRadius: "3px",
  flexDirection: "column",
  minWidth: "200px",
  "&:last-child": {
    paddingBottom: "20px",
  },
};

interface IProps {
  dispatch: ThunkDispatch;
  dimension: ClientDimension;
  validateName: (name: string) => boolean;
  isEditingView: boolean;
  selectedInstance?: ClientDimensionInstanceModal;
  onViewInstanceSidebar?: (
    dimensionInstance: ClientDimensionInstanceModal,
    dimension: Dimension
  ) => void;
  onDeleteInstanceMappings?: (dimensionInstanceId: number) => void;
  clientId: number;
  toggleSideBar: (dimension: ClientDimension | ClientDimensionInstanceModal, type: 'DIMENSION' | 'DIMENSION_INSTANCE') => void;
}

export class ClientDimensionColumn extends React.Component<IProps, any> {
  constructor(props: any) {
    super(props);

    this.isValidDimensionInstanceName =
      this.isValidDimensionInstanceName.bind(this);

    this.state = {
      dimensionName: "",
      errorMsg: "",
      isEditingDimension:
        this.props.dimension.Name === DEFAULT_CLIENT_DIMENSION_NAME,
      isSaving: false,
      isDeleting: false,
      value: this.props.dimension.Name,
      clientDimensionInstances: [],
    };
  }

  public componentDidMount() {
    if (this.props.dimension.instances) {
      this.setState({
        clientDimensionInstances: this.props.dimension.instances,
      });
    }
  }

  public render() {
    const btnClasses = classnames(Classes.LARGE, Classes.MINIMAL);
    const { dimension, selectedInstance } = this.props;
    const { isEditingDimension } = this.state;
    const isAddingDimensionInstance = (
      this.state.clientDimensionInstances ?? []
    ).some(
      (di: ClientDimensionInstanceModal) =>
        DEFAULT_CLIENT_DIMENSION_INSTANCE_NAME === di.Name
    );

    return (
      <Flex px={3} pt={3} css={columnCSS}>
        <Space mb={3}>
          <div
            style={{
              boxShadow:
                "rgba(16, 22, 26, 0.1) 0px 0px 0px 1px, rgba(16, 22, 26, 0) 0px 0px 0px, rgba(16, 22, 26, 0.2) 0px 1px 1px",
              borderRadius: "3px",
            }}
          >
            {/* {!isEditingView && (
              <Flex justifyContent="center">
                <Icon icon="drag-handle-horizontal" />
              </Flex>
            )} */}
            <Flex justifyContent={"center"}>
              {isEditingDimension ? (
                <this.DimensionEditing />
              ) : (
                <Flex
                  justifyContent={"space-between"}
                  width={1}
                  css={{ padding: "5px 16px" }}
                >
                  <Text className="bp4-text-muted bp4-text-large">
                    {dimension.Name}
                  </Text>
                  <Icon 
                    icon="flow-branch" 
                    className={Classes.TEXT_MUTED}
                    style={{ cursor: "pointer" }}
                    onClick={() => this.props.toggleSideBar(dimension, 'DIMENSION')}
                  />
                </Flex>
              )}
            </Flex>
            <Button
              text={"Add Instance"}
              rightIcon="plus"
              className={btnClasses}
              onClick={this.createInstance}
              disabled={isAddingDimensionInstance || isEditingDimension}
              fill
            />
          </div>
          {(this.state.clientDimensionInstances ?? []).map(
            (instance: ClientDimensionInstanceModal, i: any) => (
              <ClientDimensionInstance
                key={`${instance.id}-${i}`}
                dimensionInstance={instance}
                dimension={dimension}
                dispatch={this.props.dispatch}
                validateName={this.isValidDimensionInstanceName}
                clientId={this.props.clientId}
                addNewDimInstance={(instance: ClientDimensionInstanceModal) => {
                  const newInstances = [
                    ...this.state.clientDimensionInstances.filter(
                      (i: ClientDimensionInstanceModal) =>
                        i.Name !== DEFAULT_CLIENT_DIMENSION_INSTANCE_NAME
                    ),
                    instance,
                  ];
                  this.setState({ clientDimensionInstances: newInstances });
                }}
                removeDimInstance={() => {
                  const newInstances = [
                    ...this.state.clientDimensionInstances.filter(
                      (i: ClientDimensionInstanceModal) =>
                        i.Name !== DEFAULT_CLIENT_DIMENSION_INSTANCE_NAME
                    ),
                  ];
                  this.setState({ clientDimensionInstances: newInstances });
                }}
                isHighlighted={
                  selectedInstance !== undefined &&
                  instance.id === selectedInstance.id
                }
                toggleSideBar={(dimensionInstance) => this.props.toggleSideBar(dimensionInstance, 'DIMENSION_INSTANCE')}
              />
            )
          )}
          <div style={{ height: "20px", padding: "20px" }} />
        </Space>
      </Flex>
    );
  }

  private DimensionEditing: React.FC = () => {
    return (
      <form onSubmit={this.handleDimensionEdit}>
        <Flex>
          <InputGroup
            type="text"
            onChange={this.handleDimensionChange}
            placeholder="Dimension Name"
            required={true}
            autoFocus={true}
            maxLength={MAX_INSTANCE_NAME_LENGTH}
            defaultValue={
              this.props.dimension.Name !== DEFAULT_CLIENT_DIMENSION_NAME
                ? this.props.dimension.Name
                : ""
            }
          />
          <ButtonGroup minimal={true}>
            {!this.props.isEditingView && (
              <Button
                icon="trash"
                loading={this.state.isDeleting}
                onClick={this.handleDimensionDelete}
                intent={Intent.DANGER}
              />
            )}
            <Button icon="tick" type="submit" loading={this.state.isSaving} />
          </ButtonGroup>
        </Flex>
        <Text className={Classes.TEXT_SMALL} style={{color: 'red', padding: '6px 12px 0'}}>{this.state.errorMsg}</Text>
      </form>
    );
  };

  private handleDimensionEdit = (event: any) => {
    event.preventDefault();
    if (this.props.validateName(this.state.dimensionName)) {
      this.setState({ isSaving: true });
      this.props
        .dispatch(
          createClientDimensionData(
            this.props.clientId,
            this.state.dimensionName || this.state.value
          )
        )
        .then(() => {
          this.setState({ isEditingDimension: false, isSaving: false });
          Toaster.show({
            message: "Dimension created successfully",
            intent: Intent.SUCCESS,
          });
        })
        .catch((error) => {
          this.setState({ isSaving: false });
          Toaster.show({
            message: "Error creating dimension",
            intent: Intent.DANGER,
          });
        });
    } else {
      this.setState({ errorMsg: "A Dimension already exists with this name." });
    }
  };

  private handleDimensionChange = (event: any) => {
    this.setState({ dimensionName: R.trim(event.target.value) });
  };

  private handleDimensionDelete = () => {
    this.setState({ isEditingDimension: false });
    this.props.dispatch(clientDimensionActions.clearUnsavedClientDimensions());
  };

  private isValidDimensionInstanceName = (newName: string): boolean => {
    console.log({ newName, instances: this.state.clientDimensionInstances });

    return !(this.state.clientDimensionInstances ?? []).some(
      (dim: ClientDimensionInstanceModal) => {
        return (
          R.toLower(dim.Name || DEFAULT_CLIENT_DIMENSION_INSTANCE_NAME) ===
          R.toLower(newName)
        );
      }
    );
  };

  private createInstance = () => {
    this.setState({
      clientDimensionInstances: [
        ...this.state.clientDimensionInstances,
        { Name: DEFAULT_CLIENT_DIMENSION_INSTANCE_NAME },
      ],
    });
  };
}

export default connect()(ClientDimensionColumn);
